import { type SVGProps } from 'react';
import { cn } from '@/components/ui/cn';

export interface LodestarMarkProps extends Omit<SVGProps<SVGSVGElement>, 'viewBox' | 'xmlns'> {
  /**
   * Size in CSS pixels (used for width AND height). Defaults to 24.
   * Override with className for responsive sizes.
   */
  size?: number;
  /**
   * Visual treatment.
   *  - `default`: terra-accent North + ink body (brand colors).
   *  - `mono`:    single-tone, inherits currentColor — for tinted contexts.
   */
  variant?: 'default' | 'mono';
}

/**
 * Lodestar brand mark — an 8-point compass star.
 *
 * The brand name is a synonym for "pole star" (the guiding navigation star).
 * The mark expresses that with a stylized maritime compass rose: the North
 * point is highlighted in terra-600 (the "lodestar" the user is sailing
 * toward), the other three cardinal points are ink-900, and four shorter
 * intercardinal rays anchor the geometry without crowding the silhouette.
 *
 * Designed to read at 16px (favicon) through 200px+ (hero / share images)
 * without re-tracing. Stroke widths are absolute so we don't need
 * `vector-effect: non-scaling-stroke`.
 */
export function LodestarMark({
  size = 24,
  variant = 'default',
  className,
  ...rest
}: LodestarMarkProps) {
  const ink = variant === 'mono' ? 'currentColor' : '#1C1A16';
  const accent = variant === 'mono' ? 'currentColor' : '#B4502A';
  const ringOpacity = variant === 'mono' ? 0.35 : 1;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role="img"
      aria-hidden={rest['aria-hidden'] ?? true}
      className={cn('shrink-0', className)}
      {...rest}
    >
      {/* Filled ink disc — bold silhouette that survives at 16px favicon
          scale. The compass needle is drawn IN PAPER COLOR on top so the
          mark reads as a single solid coin from a distance. */}
      <circle cx="32" cy="32" r="30" fill={ink} opacity={ringOpacity} />

      {/* Horizontal needle (East-West) — paper / mono inverse */}
      <path
        d="M10 32 L32 35 L54 32 L32 29 Z"
        fill={variant === 'mono' ? '#FAF7F0' : '#FAF7F0'}
      />

      {/* South needle — paper inverse, slightly shorter than North so the
          "pointing up" hierarchy reads */}
      <path
        d="M32 54 L36 32 L28 32 Z"
        fill={variant === 'mono' ? '#FAF7F0' : '#FAF7F0'}
      />

      {/* North needle — terra accent, longest + boldest = the "lodestar".
          Reads as the visual hierarchy even at favicon scale. */}
      <path d="M32 6 L37 32 L27 32 Z" fill={accent} />

      {/* Center pivot — small paper dot inside the needle convergence so
          the silhouette stays clean (not a noisy hub) */}
      <circle cx="32" cy="32" r="2.5" fill={variant === 'mono' ? ink : ink} />
    </svg>
  );
}
